import React, { useEffect, useState } from 'react';
import { Loader2, Eye, EyeOff, Copy, Check, ShieldAlert, KeyRound } from 'lucide-react';
import { api } from '../lib/api';

interface ClaveIa {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  key: string | null;
  calls: number;
  tokens: number;
  lastUsedAt: string | null;
}

/**
 * Panel del mega guardián: las claves de IA de la plataforma y su consumo.
 *
 * Las claves llegan del servidor en claro —es el único sitio donde eso pasa— y
 * aun así se muestran tapadas por defecto. No es teatro: este panel se abre
 * muchas veces con la pantalla compartida o con alguien detrás, y dejar diez
 * credenciales ajenas a la vista sin quererlo es demasiado fácil.
 */
export const GuardianPanel: React.FC = () => {
  const [claves, setClaves] = useState<ClaveIa[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [visibles, setVisibles] = useState<Set<string>>(new Set());
  const [copiada, setCopiada] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ keys: ClaveIa[] }>('/admin/ai-keys');
        setClaves(data.keys);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo cargar el panel.');
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const alternar = (id: string) =>
    setVisibles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const copiar = async (fila: ClaveIa) => {
    if (!fila.key) return;
    try {
      await navigator.clipboard.writeText(fila.key);
      setCopiada(fila.id);
      setTimeout(() => setCopiada(null), 2000);
    } catch {
      /* sin permiso de portapapeles: queda el botón de mostrar */
    }
  };

  const totalLlamadas = claves.reduce((a, c) => a + c.calls, 0);
  const totalTokens = claves.reduce((a, c) => a + c.tokens, 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-5">
      <header className="flex items-start gap-3">
        <ShieldAlert className="w-7 h-7 text-ember-400 shrink-0 mt-1" />
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold">Claves de IA</h1>
          <p className="text-ink-400 text-sm mt-0.5">
            Claves de Google AI Studio guardadas por la gente, y lo que llevan consumido.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Resumen etiqueta="Claves guardadas" valor={claves.length.toLocaleString('es')} />
        <Resumen etiqueta="Generaciones" valor={totalLlamadas.toLocaleString('es')} />
        <Resumen etiqueta="Tokens" valor={totalTokens.toLocaleString('es')} />
      </div>

      {error && <p className="text-danger-400 text-sm">{error}</p>}

      {cargando ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
        </div>
      ) : claves.length === 0 ? (
        <div className="surface p-12 text-center space-y-2">
          <KeyRound className="w-9 h-9 mx-auto text-ink-600" />
          <p className="text-ink-300 text-sm">Todavía nadie ha guardado una clave de IA.</p>
        </div>
      ) : (
        <div className="surface divide-y divide-ink-800">
          {claves.map((fila) => (
            <article key={fila.id} className="p-4 flex flex-wrap items-center gap-x-4 gap-y-3">
              <div className="min-w-[10rem] flex-1">
                <p className="font-semibold text-ink-100 text-sm truncate">{fila.displayName}</p>
                <p className="text-ink-500 text-xs truncate">
                  {fila.username}
                  {fila.email && ` · ${fila.email}`}
                </p>
              </div>

              <div className="flex items-center gap-1.5">
                <code className="px-2.5 py-1.5 rounded-lg bg-ink-850 border border-ink-700 text-[0.75rem] font-mono text-ink-200">
                  {visibles.has(fila.id) ? fila.key : enmascarada(fila.key)}
                </code>
                <button
                  onClick={() => alternar(fila.id)}
                  className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
                  aria-label={visibles.has(fila.id) ? 'Ocultar clave' : 'Mostrar clave'}
                >
                  {visibles.has(fila.id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => void copiar(fila)}
                  className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
                  aria-label="Copiar clave"
                >
                  {copiada === fila.id ? (
                    <Check className="w-4 h-4 text-success-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>

              <dl className="flex items-center gap-5 text-xs">
                <Dato etiqueta="Generaciones" valor={fila.calls.toLocaleString('es')} />
                <Dato etiqueta="Tokens" valor={fila.tokens.toLocaleString('es')} />
                <Dato etiqueta="Último uso" valor={cuando(fila.lastUsedAt)} />
              </dl>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

/** Tapada salvo los cuatro últimos, igual que se le muestra a su dueño. */
function enmascarada(clave: string | null): string {
  if (!clave) return '—';
  return `${'•'.repeat(Math.max(4, clave.length - 4))}${clave.slice(-4)}`;
}

function cuando(iso: string | null): string {
  if (!iso) return 'Nunca';
  const fecha = new Date(iso);
  const minutos = Math.round((Date.now() - fecha.getTime()) / 60000);
  if (minutos < 1) return 'Ahora mismo';
  if (minutos < 60) return `Hace ${minutos} min`;
  if (minutos < 1440) return `Hace ${Math.round(minutos / 60)} h`;
  return fecha.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
}

const Resumen: React.FC<{ etiqueta: string; valor: string }> = ({ etiqueta, valor }) => (
  <div className="surface p-4">
    <p className="text-ink-500 text-xs uppercase tracking-wider">{etiqueta}</p>
    <p className="text-2xl font-display font-bold font-mono tabular-nums mt-0.5">{valor}</p>
  </div>
);

const Dato: React.FC<{ etiqueta: string; valor: string }> = ({ etiqueta, valor }) => (
  <div>
    <dt className="text-ink-500">{etiqueta}</dt>
    <dd className="text-ink-200 font-medium tabular-nums">{valor}</dd>
  </div>
);
